import {
  PhoneLogEntry,
  TellescopeModelFromName,
  useAuth,
  useTellescopeMutation,
  useTwilioPhoneLogsQuery,
  UseTwilioPhoneLogsQueryResult,
} from "@tellescope/tanstack-query";
import { useDateFnsFormattersWithLocale, type VirtualizedColumnDef, VirtualizedDataTableProps } from "@tellescope/ui";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useTableView, TELLESCOPE_2_TABLE_VIEW_PREFIX } from "./useTableView";
import { useTableViewConfig, type UseTableViewConfigResult } from "./useTableViewConfig";
import { DEFAULT_PHONE_LOGS_VIEW, usePhoneLogsDefaultViewShallowStore } from "../store";
import { type PhoneLogsFilters, countPhoneLogsActiveFilters } from "../components/phonelogs/phoneLogsFilters";
import { formatPhone, normalizeTwilioPhone } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { i18n } from "@tellescope/i18n";

export interface UsePhoneLogsTableArgs {
  viewId?: string;
  setViewId?: Dispatch<SetStateAction<string>>;
}

const getStatusTranslations = (): Record<string, string> => ({
  delivered: i18n.t("delivered", { ns: "settings" }),
  undelivered: i18n.t("undelivered", { ns: "settings" }),
  failed: i18n.t("failed", { ns: "settings" }),
});

export interface UsePhoneLogsTableResult extends UseTableViewConfigResult<PhoneLogsFilters> {
  logsQuery: UseTwilioPhoneLogsQueryResult;
  virtualizedTableProps: Partial<VirtualizedDataTableProps<PhoneLogEntry>> &
    Pick<VirtualizedDataTableProps<PhoneLogEntry>, "data" | "columns">;
  views: TellescopeModelFromName<"table_views">[];
  selectedView: TellescopeModelFromName<"table_views">;
  viewId: string;
  setViewId: Dispatch<SetStateAction<string>>;
  isLoadingViews: boolean;
  createTableViewMutation: ReturnType<typeof useTellescopeMutation<"table_views", "createOne">>;
  deleteTableViewMutation: ReturnType<typeof useTellescopeMutation<"table_views", "deleteOne">>;
  directionOptions: string[];
  statusOptions: string[];
  TELLESCOPE_2_TABLE_VIEW_PREFIX: typeof TELLESCOPE_2_TABLE_VIEW_PREFIX;
}

export const usePhoneLogsTable = ({
  viewId: providedViewId,
  setViewId,
}: UsePhoneLogsTableArgs = {}): UsePhoneLogsTableResult => {
  const { t } = useTranslation("settings");
  const { format } = useDateFnsFormattersWithLocale();
  const { currentUser } = useAuth();
  const userId = currentUser.id;

  const [searchQuery, setSearchQuery] = useState("");

  const { defaultViewByUserId, setUserDefaultView } = usePhoneLogsDefaultViewShallowStore(
    "defaultViewByUserId",
    "setUserDefaultView"
  );

  const fieldToColumnConfigMap = useMemo<Record<string, Partial<VirtualizedColumnDef<PhoneLogEntry>>>>(
    () => ({
      Date: {
        accessorFn: (row: PhoneLogEntry) => row.date.toLocaleString(),
        label: t("Date"),
        width: "220px",
        cell: ({ row }) => <div className="text-sm truncate">{format(row.original.date, "MMM d, yyyy, h:mm a")}</div>,
      },
      From: {
        accessorKey: "from",
        label: t("From"),
        cell: ({ row }) => (
          <div className="text-sm truncate">
            {formatPhone(normalizeTwilioPhone(row.original.from)) || row.original.from}
          </div>
        ),
      },
      To: {
        accessorKey: "to",
        label: t("To"),
        cell: ({ row }) => (
          <div className="text-sm truncate">
            {formatPhone(normalizeTwilioPhone(row.original.to)) || row.original.to}
          </div>
        ),
      },
      Direction: {
        accessorKey: "direction",
        label: t("Direction"),
        cell: ({ row }) => (
          <div className="text-sm truncate">
            {row.original.direction.includes("outbound") ? t("outbound") : t("inbound")}
          </div>
        ),
      },
      Status: {
        accessorKey: "status",
        label: t("Status"),
        cell: ({ row }) => (
          <div className="text-sm truncate">{getStatusTranslations()[row.original.status] || row.original.status}</div>
        ),
      },
      Type: {
        accessorKey: "type",
        label: t("Type"),
        width: "100px",
        cell: ({ row }) => (
          <div className="text-sm truncate">{row.original.type === "Voice" ? t("Call") : t("SMS")}</div>
        ),
      },
      Duration: {
        accessorKey: "duration",
        label: t("Duration"),
        cell: ({ row }) => (
          <div className="text-sm truncate">
            {row.original.duration !== undefined ? t("{{seconds}}s", { seconds: row.original.duration }) : "—"}
          </div>
        ),
      },
    }),
    [format, t]
  );

  const {
    views,
    selectedView,
    columns,
    viewId,
    setViewId: setViewIdInternal,
    isLoadingViews,
    isDefaultView,
  } = useTableView<PhoneLogEntry>({
    page: "T2-PhoneLogs",
    defaultView: defaultViewByUserId[userId] ?? DEFAULT_PHONE_LOGS_VIEW,
    fieldToColumnConfigMap,
    viewId: providedViewId,
    setViewId,
    sortable: false,
  });

  const activeFilters = useMemo<PhoneLogsFilters>(() => {
    const { sorting: _sorting, ...filters } = selectedView.filter ?? {};
    return filters;
  }, [selectedView.filter]);

  const viewConfig = useTableViewConfig<PhoneLogsFilters>({
    isDefaultView,
    userId,
    selectedView,
    columns,
    allColumnConfigMap: fieldToColumnConfigMap,
    defaultView: DEFAULT_PHONE_LOGS_VIEW,
    setUserDefaultView,
    activeFilters,
    countActiveFilters: countPhoneLogsActiveFilters,
  });

  const logsQuery = useTwilioPhoneLogsQuery({
    from: activeFilters.dateRange?.from !== undefined ? new Date(activeFilters.dateRange.from) : undefined,
    to: activeFilters.dateRange?.to !== undefined ? new Date(activeFilters.dateRange.to) : undefined,
  });

  // Distinct direction/status values across loaded rows — power the filter dialog's options.
  const { directionOptions, statusOptions } = useMemo(() => {
    const directions = new Set<string>();
    const statuses = new Set<string>();
    for (const log of logsQuery.logs) {
      if (log.direction) directions.add(log.direction);
      if (log.status) statuses.add(log.status);
    }
    return {
      directionOptions: Array.from(directions).sort(),
      statusOptions: Array.from(statuses).sort(),
    };
  }, [logsQuery.logs]);

  // Type/Direction/Status filters are applied client-side (Twilio can't filter these server-side).
  const filteredLogs = useMemo(() => {
    const { types, directions, statuses } = viewConfig.activeFilters;
    if (!types?.length && !directions?.length && !statuses?.length) return logsQuery.logs;
    return logsQuery.logs.filter((log) => {
      if (types?.length && !types.includes(log.type)) return false;
      if (directions?.length && !directions.includes(log.direction)) return false;
      if (statuses?.length && !statuses.includes(log.status)) return false;
      return true;
    });
  }, [logsQuery.logs, viewConfig.activeFilters]);

  const createTableViewMutation = useTellescopeMutation({ model: "table_views", method: "createOne" });
  const deleteTableViewMutation = useTellescopeMutation({ model: "table_views", method: "deleteOne" });

  const virtualizedTableProps = {
    data: filteredLogs,
    columns,
    getRowId: (row: PhoneLogEntry) => row.id,
    enableInternalFiltering: true,
    globalFilter: searchQuery,
    onGlobalFilterChange: setSearchQuery,
    sorting: viewConfig.sorting,
    setSorting: viewConfig.onSortingChange,
    onLoadMore: () => {
      logsQuery.fetchNextPage();
    },
    hasMoreData: logsQuery.hasNextPage,
    isEmpty: logsQuery.isFetched && filteredLogs.length === 0,
    isLoadingMore: logsQuery.isLoading || logsQuery.isFetchingNextPage,
    headerClassName: "bg-gray-50",
  } satisfies Partial<VirtualizedDataTableProps<PhoneLogEntry>>;

  return {
    logsQuery,
    virtualizedTableProps,
    views,
    selectedView,
    viewId,
    setViewId: setViewIdInternal,
    isLoadingViews,
    createTableViewMutation,
    deleteTableViewMutation,
    directionOptions,
    statusOptions,
    TELLESCOPE_2_TABLE_VIEW_PREFIX,
    ...viewConfig,
  };
};
